import apiClient from '../config/api';
import type {
  AgreementDetail,
  AgreementCalculatedParam,
  AgreementDocumentInfo,
  AgreementVersionInfo,
  Parameter,
} from '../types';

export interface ListAgreementsParams {
  hospitalUuid?: string;
  active?: string;
}

function mapCalculatedParam(data: Record<string, unknown>): AgreementCalculatedParam {
  return {
    calculatedParameterId: data.calculated_parameter_id as number,
    agreementId: data.agreement_id as number,
    parameterName: data.parameter_name as string,
    naturalLanguageFormula: data.natural_language_formula as string,
    executableFormula: data.executable_formula as string,
    executionOrder: data.execution_order as number,
    createdAt: data.created_at as string,
  };
}

function mapDocument(data: Record<string, unknown>): AgreementDocumentInfo {
  return {
    documentId: data.document_id as number,
    agreementId: data.agreement_id as number,
    fileName: data.file_name as string,
    filePath: data.file_path as string,
    fileType: data.file_type as string,
    fileSize: data.file_size as number,
    uploadedBy: data.uploaded_by as string,
    uploadedAt: data.uploaded_at as string,
  };
}

function mapInputParameter(data: Record<string, unknown>): Parameter {
  return {
    parameterId: data.parameter_id as number,
    parameterName: data.parameter_name as string,
    parameterType: data.parameter_type as 'INPUT' | 'CALCULATED',
    dataType: data.data_type as Parameter['dataType'],
    naturalLanguageFormula: data.natural_language_formula as string | undefined,
    executableFormula: data.executable_formula as string | undefined,
    validationRules: data.validation_rules as Parameter['validationRules'],
    dropdownValues: data.dropdown_values as string[] | undefined,
    displayOrder: data.display_order as number,
    isActive: data.is_active as boolean,
    createdBy: data.created_by as string,
    modifiedBy: data.modified_by as string,
    createdAt: data.created_at as string,
    modifiedAt: data.modified_at as string,
  };
}

function mapAgreement(data: Record<string, unknown>): AgreementDetail {
  return {
    agreementId: data.agreement_id as number,
    hospitalUuid: data.hospital_uuid as string,
    hospitalName: data.hospital_name as string | undefined,
    validFrom: data.valid_from as string,
    validTill: data.valid_till as string,
    hospitalShareNlFormula: data.hospital_share_nl_formula as string,
    hospitalShareExecutable: data.hospital_share_executable as string,
    arApNlFormula: data.ar_ap_nl_formula as string,
    arApExecutable: data.ar_ap_executable as string,
    version: data.version as number,
    createdBy: data.created_by as string,
    modifiedBy: data.modified_by as string,
    createdAt: data.created_at as string,
    modifiedAt: data.modified_at as string,
    inputParameters: Array.isArray(data.inputParameters)
      ? data.inputParameters.map(mapInputParameter)
      : undefined,
    calculatedParameters: Array.isArray(data.calculatedParameters)
      ? data.calculatedParameters.map(mapCalculatedParam)
      : undefined,
    documents: Array.isArray(data.documents)
      ? data.documents.map(mapDocument)
      : undefined,
  };
}

function mapVersion(data: Record<string, unknown>): AgreementVersionInfo {
  return {
    versionId: data.version_id as number,
    agreementId: data.agreement_id as number,
    versionNumber: data.version_number as number,
    hospitalShareNlFormula: data.hospital_share_nl_formula as string,
    hospitalShareExecutable: data.hospital_share_executable as string,
    arApNlFormula: data.ar_ap_nl_formula as string,
    arApExecutable: data.ar_ap_executable as string,
    parametersSnapshot: data.parameters_snapshot,
    changedBy: data.changed_by as string,
    changedAt: data.changed_at as string,
    changeDescription: data.change_description as string | undefined,
  };
}

export async function listAgreements(
  params: ListAgreementsParams = {}
): Promise<{ agreements: AgreementDetail[] }> {
  const query: Record<string, string> = {};
  if (params.hospitalUuid) query.hospitalUuid = params.hospitalUuid;
  if (params.active) query.active = params.active;

  const response = await apiClient.get('/api/agreements', { params: query });
  const data = response.data;

  return {
    agreements: (data.agreements || []).map(mapAgreement),
  };
}

export async function getAgreement(agreementId: number): Promise<AgreementDetail> {
  const response = await apiClient.get(`/api/agreements/${agreementId}`);
  return mapAgreement(response.data.agreement || response.data);
}

export async function createAgreement(body: {
  hospitalUuid: string;
  validFrom: string;
  validTill: string;
  hospitalShareNlFormula: string;
  arApNlFormula: string;
  inputParameterIds?: number[];
  calculatedParameters?: {
    parameterName: string;
    naturalLanguageFormula: string;
  }[];
}): Promise<AgreementDetail> {
  const response = await apiClient.post('/api/agreements', body);
  return mapAgreement(response.data.agreement || response.data);
}

export async function updateAgreement(
  agreementId: number,
  updates: {
    hospitalUuid?: string;
    validFrom?: string;
    validTill?: string;
    hospitalShareNlFormula?: string;
    arApNlFormula?: string;
    inputParameterIds?: number[];
    calculatedParameters?: {
      parameterName: string;
      naturalLanguageFormula: string;
    }[];
    changeDescription?: string;
  }
): Promise<AgreementDetail> {
  const response = await apiClient.put(`/api/agreements/${agreementId}`, updates);
  return mapAgreement(response.data.agreement || response.data);
}

export async function getActiveAgreement(
  hospitalUuid: string,
  date?: string
): Promise<AgreementDetail | null> {
  const query: Record<string, string> = { hospitalUuid };
  if (date) query.date = date;

  const response = await apiClient.get('/api/agreements/active', { params: query });
  if (!response.data.agreement) return null;
  return mapAgreement(response.data.agreement);
}

export async function listAgreementVersions(
  agreementId: number
): Promise<AgreementVersionInfo[]> {
  const response = await apiClient.get(`/api/agreements/${agreementId}/versions`);
  return (response.data.versions || []).map(mapVersion);
}

export async function getAgreementVersion(
  agreementId: number,
  versionNumber: number
): Promise<AgreementVersionInfo> {
  const response = await apiClient.get(
    `/api/agreements/${agreementId}/versions/${versionNumber}`
  );
  return mapVersion(response.data.version || response.data);
}

export async function uploadAgreementDocument(
  agreementId: number,
  file: File
): Promise<AgreementDocumentInfo> {
  const formData = new FormData();
  formData.append('document', file);

  const response = await apiClient.post(
    `/api/agreements/${agreementId}/documents`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return mapDocument(response.data.document || response.data);
}

export async function downloadAgreementDocument(
  agreementId: number,
  documentId: number
): Promise<Blob> {
  const response = await apiClient.get(
    `/api/agreements/${agreementId}/documents/${documentId}`,
    { responseType: 'blob' }
  );
  return response.data;
}

export async function deleteAgreementDocument(
  agreementId: number,
  documentId: number
): Promise<void> {
  await apiClient.delete(`/api/agreements/${agreementId}/documents/${documentId}`);
}

export async function validateFormula(body: {
  formula: string;
  context?: Record<string, unknown>;
}): Promise<{ valid: boolean; errors?: string[] }> {
  const response = await apiClient.post('/api/agreements/validate-formula', body);
  return response.data;
}

export async function convertFormula(body: {
  formula: string;
  inputParameterNames?: string[];
  calculatedParameterNames?: string[];
}): Promise<{ executableFormula: string; referencedVariables: string[] }> {
  const response = await apiClient.post('/api/agreements/convert-formula', body);
  return response.data;
}
